// src/utils/mapboxDataFetcher.ts
import { GEOJSON_URLS, MODEL_GEOJSON_URL, WARD_GEOJSON_URL } from '../constants/mapbox.constants';
import { GeoJson, GeoJsonFeature, MatchingApiFeature, MatchingApiResponse } from '../types/mapbox.types';

/** Human-readable file name from URL for error messages */
const shortNameFromUrl = (url: string): string => {
  try {
    const path = url.split("?")[0].split("/").pop() ?? url;
    return decodeURIComponent(path);
  } catch {
    return url;
  }
};

/**
 * Fetch with user-oriented errors (SSL, offline, HTTP errors).
 * Network-level failures (net::ERR_SSL_PROTOCOL_ERROR, etc.) surface as catch on fetch().
 */
async function fetchChecked(url: string): Promise<Response> {
  if (!url?.trim()) {
    throw new Error("Map data URL is missing. Check environment configuration.");
  }
  let res: Response;
  try {
    res = await fetch(url);
  } catch {
    const label = shortNameFromUrl(url);
    throw new Error(
      `Could not download "${label}". This is often a connection, VPN, firewall, or HTTPS issue. Refresh the page or try again shortly.`
    );
  }
  if (!res.ok) {
    const label = shortNameFromUrl(url);
    throw new Error(
      `Could not load "${label}" (HTTP ${res.status}). Refresh the page or try again later.`
    );
  }
  return res;
}
const mapTech1ToMapboxProperties = (
    tech1Feature: any,
    mapboxFeature: any
) => {
    return {
        PropertyId: tech1Feature.PropertyId,
        ApplicationStage: tech1Feature.ApplicationStage,
        ApplicationStatus: tech1Feature.ApplicationStatus,
        LodgedDate: tech1Feature.LodgedDate,
        Category: tech1Feature.Category,
        Compliance: tech1Feature.Compliance,
        Status: tech1Feature.Status,
        Description: tech1Feature.Description,
        SiteName: tech1Feature.SiteName,
        Decision: tech1Feature.Decision,
        ApplicationUrl: tech1Feature.Applicat_3,
        BuildingHeight: mapboxFeature
            ? Number(mapboxFeature.EAVE_Elev)
            : Number(tech1Feature.BuildingHeight),
        ApplicationId: tech1Feature.APPLN_ID,
        ProjectId: tech1Feature.Project_ID,
        POIGeometry: tech1Feature.POIGeometry,
        PDDAM1: tech1Feature.PDDAM1,
        PDDAM2: tech1Feature.PDDAM2,
        ELECTORATE: tech1Feature.ELECTORATE,
    };
};

export interface FetchResult {
    GeojsonData: GeoJson;
    POIData: any;
    BoundaryData: any;
}


export const fetchDatasetsAndMatchProperties = async (): Promise<FetchResult> => {
    try {
      // Fire off all requests in parallel
      const [geojsonResponses, modelRes, wardRes] = await Promise.all([
        Promise.all(GEOJSON_URLS.map((url) => fetchChecked(url))),
        fetchChecked(MODEL_GEOJSON_URL),
        fetchChecked(WARD_GEOJSON_URL),
      ]);
  
      const parseJson = async <T>(res: Response, label: string): Promise<T> => {
        try {
          return (await res.json()) as T;
        } catch {
          throw new Error(
            `Received invalid data from "${label}". Refresh the page or try again later.`
          );
        }
      };

      const datasets: GeoJson[] = await Promise.all(
        geojsonResponses.map((res, i) =>
          parseJson<GeoJson>(res, shortNameFromUrl(GEOJSON_URLS[i]))
        )
      );
  
      const matchingData = await parseJson<MatchingApiResponse>(
        modelRes,
        shortNameFromUrl(MODEL_GEOJSON_URL)
      );
      const wardRaw = await parseJson<any[]>(
        wardRes,
        shortNameFromUrl(WARD_GEOJSON_URL)
      );
  
      const boundaryData = {
        type: "FeatureCollection",
        features: wardRaw.map((item: any) => ({
          type: "Feature",
          geometry: item.json_geometry,
          properties: {
            WARD_NAME: item.WARD_NAME,
            GISID: item.GISID,
          },
        })),
      };
  
      // Create lookup for MatchingApiFeatures
      const matchingDataLookup = Object.fromEntries(
        (matchingData.features || []).map((f) => [f.properties.PropertyId, f])
      );
  
      const seenPropertyIds = new Set<string>();
      const matchingDataShortlist: MatchingApiFeature[] = [];
  
      const combinedGeojson: GeoJson = {
        type: "FeatureCollection",
        features: datasets.flatMap((dataset) =>
          dataset.features.map((feature: GeoJsonFeature) => {
            const propertyId = feature.properties.PropertyId;
            const match = matchingDataLookup[propertyId];
  
            if (match) {
              if (!seenPropertyIds.has(propertyId)) {
                matchingDataShortlist.push(match);
                seenPropertyIds.add(propertyId);
              }
  
              return {
                ...feature,
                properties: mapTech1ToMapboxProperties(match.properties, feature.properties),
              };
            }
  
            return feature;
          })
        ),
      };
  
      const poiData = {
        type: "FeatureCollection",
        features: matchingDataShortlist.map((f) => ({
          type: "Feature",
          geometry: f.properties.POIGeometry,
          properties: {
            FID: f.properties.PropertyId,
            ApplicationStage: f.properties.ApplicationStage,
            Status: f.properties.Status,
            Lodged_Dat: f.properties.LodgedDate,
            SiteName: f.properties.SiteName,
            Decision: f.properties.Decision,
            icon: f.properties.Icon,
          },
        })),
      };
  
      return {
        GeojsonData: combinedGeojson,
        POIData: poiData,
        BoundaryData: boundaryData,
      };
    } catch (err) {
      console.error("Failed to fetch and process map data:", err);
      throw err;
    }
  };
  